import { memo } from "react";
import { View } from "@src/components";
import { StyleSheet } from "react-native-unistyles";
import Animated, { type AnimatedStyle } from "react-native-reanimated";
import { VideoControlButton } from "./VideoControlButton";
import type { ViewStyle } from "react-native";

type VideoControlsOverlayProps = {
  isPlaying: boolean;
  isEnded: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  controlsVisible: boolean;
  isFullscreen: boolean;
  animatedStyle: AnimatedStyle<ViewStyle>;
  onPrevious?: () => void;
  onNext?: () => void;
  onPlayPause: () => void;
};

function getPlayButtonIcon(isPlaying: boolean, isEnded: boolean): "pause" | "play" | "refresh" {
  if (isEnded) return "refresh";
  if (isPlaying) return "pause";
  return "play";
}

export const VideoControlsOverlay = memo(
  ({
    isPlaying,
    isEnded,
    hasPrevious,
    hasNext,
    controlsVisible,
    isFullscreen,
    animatedStyle,
    onPrevious,
    onNext,
    onPlayPause,
  }: VideoControlsOverlayProps) => {
    return (
      <Animated.View
        style={[styles.overlay, animatedStyle]}
        pointerEvents={controlsVisible || isFullscreen ? "box-none" : "none"}
      >
        <View style={styles.container}>
          {/* Previous button */}
          <VideoControlButton icon="skipPrevious" onPress={onPrevious} disabled={!hasPrevious} />

          {/* Play/Pause/Restart button */}
          <VideoControlButton icon={getPlayButtonIcon(isPlaying, isEnded)} onPress={onPlayPause} size="large" />

          {/* Next button */}
          <VideoControlButton icon="skipNext" onPress={onNext} disabled={!hasNext} />
        </View>
      </Animated.View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing["5xl"],
  },
}));
